import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { MaskLines, Reveal, EASE } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { INFO, IMAGES, CHAPTERS, MENU_SAVORY } from "@/data/site";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden pt-24 pb-10">
        {/* Floating clipped food image */}
        <motion.div
          style={{ y: imgY, scale: imgScale }}
          className="absolute right-[4%] top-[16%] w-[46vw] max-w-[560px] aspect-[3/4] z-10 hidden sm:block"
          initial={{ opacity: 0, clipPath: "inset(100% 0 0 0)" }}
          animate={{ opacity: 1, clipPath: "inset(0% 0 0 0)" }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.5 }}
        >
          <img src={IMAGES.heroBowl} alt="Banga Bowls Jollof" className="w-full h-full object-cover grayscale-[15%] contrast-125" />
          <div className="absolute inset-0 ring-1 ring-inset ring-line" />
          <span className="absolute -left-3 top-6 bg-mango text-base font-mono text-xs px-3 py-1 uppercase tracking-widest">Jollof · CHF 16.50</span>
        </motion.div>

        <div className="relative z-20 mx-auto max-w-[1400px] w-full px-5 sm:px-8">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-muted mb-6"
          >
            Pop-Up · Afrikanisches Street Food · {INFO.city}
          </motion.p>

          <motion.h1 style={{ y: textY }} className="font-display uppercase leading-[0.82] tracking-tight text-[19vw] sm:text-[15vw] lg:text-[13vw]">
            <MaskLines lines={["Banga"]} delay={0.15} className="block" />
            <MaskLines lines={[<span key="b">Bowls<span className="text-harissa glow-harissa">.</span></span>]} delay={0.28} className="block" />
          </motion.h1>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, ease: EASE }}
              className="md:col-span-6 font-sans text-lg text-muted max-w-md"
            >
              Würzig, ehrlich und zum Teilen. Ein paar richtig gute Gerichte, serviert dort, wo der Abend sowieso schon stattfindet.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, ease: EASE }}
              className="md:col-span-6 flex flex-wrap gap-4 md:justify-end"
            >
              <Link to="/menu" data-testid="hero-menu-btn" className="group inline-flex items-center gap-2 bg-harissa text-base font-mono text-sm uppercase tracking-widest px-7 py-4 hover:bg-ink transition-colors duration-200">
                Zur Karte <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/reservierung" data-testid="hero-reserve-btn" className="inline-flex items-center gap-2 border border-line text-ink font-mono text-sm uppercase tracking-widest px-7 py-4 hover:border-harissa hover:text-harissa transition-colors duration-200">
                Reservieren
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* IDEA / manifesto teaser */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 sm:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-harissa mb-4">Die Idee</p>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <Reveal delay={0.1}>
              <h2 className="font-head font-[900] text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">
                Afrika, in der<br /><span className="text-stroke-harissa">Bar nebenan.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="font-sans text-lg text-muted mt-8 max-w-2xl">
                Banga Bowls bringt afrikanisches Street Food dorthin, wo am Abend ohnehin alle sind: in die Bar. Kein steifes Restaurant, keine endlose Karte, dafür ein paar Gerichte, die richtig gut sind. Auf dem Teller trifft Nordafrika auf Westafrika, würzig, farbig und gemacht, um geteilt zu werden.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <Link to="/story" className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-widest mt-8 text-ink hover:text-mango transition-colors" data-testid="home-story-link">
                Ganze Story <ArrowUpRight size={16} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Full-bleed fire parallax break */}
      <ParallaxBreak />

      {/* MENU teaser */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 sm:py-32">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
          <div>
            <Reveal><p className="font-mono text-xs uppercase tracking-[0.2em] text-harissa mb-4">Die Karte</p></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-head font-[900] text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">Herzhaft &amp; würzig.</h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <Link to="/menu" className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-widest hover:text-mango transition-colors" data-testid="home-menu-link">
              Ganze Karte <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>

        <div className="border-t border-line">
          {MENU_SAVORY.items.map((item, i) => (
            <Reveal key={item.n} delay={i * 0.05}>
              <div className="group grid grid-cols-12 gap-4 items-baseline border-b border-line py-7 hover:bg-surface transition-colors duration-300 px-1 sm:px-4" data-testid={`home-menu-item-${item.n}`}>
                <span className="col-span-2 sm:col-span-1 font-display text-2xl text-muted group-hover:text-harissa transition-colors">{item.n}</span>
                <div className="col-span-10 sm:col-span-7">
                  <h3 className="font-head font-[800] text-2xl sm:text-3xl uppercase">{item.name}</h3>
                  <p className="font-mono text-xs uppercase tracking-widest text-muted mt-1">{item.origin}</p>
                </div>
                <p className="hidden sm:block sm:col-span-3 font-sans text-sm text-muted">{item.desc.slice(0, 78)}…</p>
                <span className="col-span-12 sm:col-span-1 font-mono text-lg text-mango text-right mt-2 sm:mt-0">{item.price}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CHAPTERS teaser */}
      <section className="border-t border-line bg-surface">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 sm:py-32">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1} className="bg-surface">
                <div className="p-8 sm:p-10 h-full">
                  <span className="font-display text-6xl text-harissa">{c.n}</span>
                  <h3 className="font-head font-[800] text-2xl uppercase mt-4">{c.title}</h3>
                  <p className="font-sans text-muted mt-3">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA />
    </div>
  );
}

function ParallaxBreak() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section ref={ref} className="relative h-[60vh] overflow-hidden border-y border-line">
      <motion.div style={{ y }} className="absolute inset-0 -top-[12%] h-[124%]">
        <img src={IMAGES.fire} alt="Küche im Feuer" className="w-full h-full object-cover contrast-125 brightness-75" />
      </motion.div>
      <div className="absolute inset-0 bg-base/40" />
      <div className="relative z-10 h-full flex items-center justify-center px-5">
        <h2 className="font-display uppercase text-center text-[13vw] leading-[0.85] text-ink glow-harissa">
          Bis spät.
        </h2>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 sm:py-32">
      <Reveal>
        <div className="border border-line p-8 sm:p-16 relative overflow-hidden">
          <div className="absolute -right-10 -bottom-16 font-display text-[26vw] leading-none text-stroke opacity-20 select-none pointer-events-none">✦</div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-harissa mb-6 relative">Reservieren</p>
          <h2 className="font-head font-[900] text-4xl sm:text-6xl uppercase leading-[0.95] relative max-w-3xl">
            Komm vorbei, bring Leute mit.
          </h2>
          <p className="font-sans text-lg text-muted mt-6 max-w-xl relative">
            Sichere dir einen Platz am Tresen. Wir melden uns und halten die Box warm.
          </p>
          <Link to="/reservierung" data-testid="cta-reserve-btn" className="inline-flex items-center gap-2 bg-harissa text-base font-mono text-sm uppercase tracking-widest px-8 py-4 mt-10 hover:bg-ink transition-colors relative">
            Tisch anfragen <ArrowRight size={16} />
          </Link>
        </div>
      </Reveal>
    </section>
  );
}
